import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { createStyles } from './ScheduledPostsSummary.styles';

export interface ScheduledPostsSummaryViewProps {
  actionsDisabled?: boolean;
  actionsLabel: string;
  attachmentCount: number;
  attachmentSummary?: string;
  manageLabel?: string;
  onManage?: () => void;
  onOpenActions?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onView: () => void;
  preview: string;
  scheduledDate: string;
  scheduledFor?: string;
  summaryLabel: string;
  title: string;
  unavailableActionsLabel: string;
  viewLabel: string;
}

export const ScheduledPostsSummaryView = ({
  actionsDisabled = false,
  actionsLabel,
  attachmentCount,
  attachmentSummary,
  manageLabel = 'Manage',
  onManage,
  onOpenActions,
  onView,
  preview,
  scheduledDate,
  scheduledFor,
  summaryLabel,
  title,
  unavailableActionsLabel,
  viewLabel,
}: ScheduledPostsSummaryViewProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);

  return (
    <Box
      aria-label={summaryLabel}
      component="article"
      data-testid="scheduled-posts-summary"
      sx={styles.card}
    >
      <Box component="header" sx={styles.header}>
        <Typography component="h2" sx={styles.headerText}>
          {summaryLabel}
        </Typography>
        <Button
          kind="secondary"
          onClick={onView}
          size="small"
          sx={styles.viewButton}
          type="button"
        >
          {viewLabel}
        </Button>
      </Box>

      <Divider sx={styles.divider} />

      <Box sx={styles.metadata}>
        <Box sx={styles.scheduledDetails}>
          <Icons.Schedule aria-hidden="true" sx={styles.scheduleIcon} />
          <Typography component="div" sx={styles.scheduledText}>
            <Box component="span">Scheduled for</Box>
            <Box component="span" sx={styles.scheduledDate}>
              <time dateTime={scheduledFor}>{scheduledDate}</time>
            </Box>
          </Typography>
        </Box>

        <Box sx={styles.metadataActions}>
          <Button
            aria-disabled={!onManage || undefined}
            endIcon={<Icons.KeyboardArrowDown aria-hidden="true" />}
            kind="secondary"
            onClick={onManage}
            size="small"
            sx={styles.changeButton}
            type="button"
          >
            {manageLabel}
          </Button>
          <IconButton
            aria-disabled={!onOpenActions || undefined}
            aria-label={onOpenActions ? actionsLabel : unavailableActionsLabel}
            disabled={actionsDisabled}
            onClick={onOpenActions}
            size="small"
            sx={styles.actionsButton}
            type="button"
          >
            <Icons.MoreVert aria-hidden="true" />
          </IconButton>
        </Box>
      </Box>

      {attachmentCount === 1 ? (
        <Typography component="p" sx={styles.postPreview}>
          {attachmentSummary}
        </Typography>
      ) : null}

      {attachmentCount > 1 ? (
        <Box
          aria-label={`${attachmentCount} attachments`}
          data-testid="scheduled-post-attachments"
          sx={styles.attachments}
        >
          <Icons.FilePresentOutlined
            aria-hidden="true"
            sx={styles.attachmentIcon}
          />
          <Typography component="span" sx={styles.attachmentText}>
            {attachmentCount > 10 ? '10+' : attachmentCount} attachments
          </Typography>
        </Box>
      ) : null}

      {title ? (
        <Typography component="h3" sx={styles.postTitle}>
          {title}
        </Typography>
      ) : null}

      {preview ? (
        <Typography component="p" sx={styles.postPreview}>
          {preview}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ScheduledPostsSummaryView;
